// Vuex module for the Combat Log Analyzer.
//
// The store holds plain, serializable view state. Everything that must not be
// wrapped in a reactive proxy - directory and file handles, the worker client,
// the monitor - lives in module scope below, because native File System Access
// objects can throw when their methods are called through a Proxy.

import {
    supportsDirectoryAccess,
    supportsFileSystemObserver,
    supportsHandlePersistence,
    pickDirectory,
    loadStoredDirectory,
    forgetDirectory,
    queryAccess,
    requestAccess,
    scanDirectory,
    getFileFromHandle,
    looksLikeWowLog,
} from '../../services/combatLog/directory'
import { createMonitor } from '../../services/combatLog/monitor'
import { getAnalyzerClient } from '../../services/combatLog/analyzerClient'
import {
    listFileRecords,
    deleteFileRecord,
    getReport,
    clearAll,
    estimateUsage,
} from '../../services/combatLog/db'
import { readSettings, writeSettings } from '../../services/combatLog/settings'

const MANUAL_DIRECTORY_ID = 'manual-uploads'
const TABS = ['overview', 'players', 'abilities', 'deaths', 'compare', 'logs']

let directoryHandle = null
let monitor = null
let client = null
let unsubscribe = []
const fileHandles = new Map()
const manualFiles = new Map()
let broadcast = null

// The newest log is opened once per page session. After that the choice is the
// user's: a scan that finds a newer log analyzes it in the background rather
// than pulling the view away from what is being read.
let openedNewestLog = false

// Logs waiting to be shown on their last run. The jump happens as soon as a
// summary exists, which may be before or after the analysis, and only for a
// log the user has just opened, so a later re-analysis leaves their choice of
// fight alone.
const focusLatest = new Set()

function makeFileId(directoryId, name) {
    return `${directoryId}::${name}`
}

// The fight someone opens a log for is the last one in it, whatever kind it
// is: in a raid night the key in the middle of the log is not what they just
// finished. Only the whole log, which spans all of them, is passed over.
function latestSegmentKey(summary) {
    const last = ((summary && summary.segments) || [])
        .filter((segment) => segment.type !== 'log')
        .sort((left, right) => left.startTs - right.startTs)
        .pop()
    return last ? last.key : 'log'
}

function toFileEntry(directoryId, scanned, record) {
    const id = makeFileId(directoryId, scanned ? scanned.name : record.name)
    const summary = record ? record.summary : null
    const size = scanned ? scanned.size : (record ? record.size : 0)
    const lastModified = scanned ? scanned.lastModified : (record ? record.lastModified : 0)

    let status = 'new'
    if (record) {
        if (record.status === 'error') status = 'error'
        else if (record.status === 'processing') status = 'processing'
        else if (record.offset > 0 && size > record.size) status = 'updated'
        else if (record.offset > 0 && size < record.offset) status = 'updated'
        else if (record.offset > 0) status = 'ready'
    }

    return {
        id,
        name: scanned ? scanned.name : record.name,
        directoryId,
        size,
        lastModified,
        available: Boolean(scanned),
        isWowLog: scanned ? scanned.isWowLog : looksLikeWowLog(record.name),
        status,
        offset: record ? record.offset : 0,
        analyzedAt: record ? record.analyzedAt : 0,
        error: record ? record.error : '',
        summary,
    }
}

function getClient() {
    if (!client) client = getAnalyzerClient()
    return client
}

export default {
    namespaced: true,

    state() {
        const settings = readSettings()
        return {
            initialized: false,
            supported: {
                directory: false,
                observer: false,
                persistence: false,
            },
            mode: 'manual',
            usesWorker: true,
            directory: {
                id: '',
                name: settings.directoryName || '',
                connected: false,
                permission: 'prompt',
                connectedAt: 0,
            },
            monitor: {
                mode: 'idle',
                paused: false,
                running: false,
                lastScanAt: 0,
                lastScanReason: '',
                intervalMs: 0,
                observerActive: false,
                scanning: false,
                error: '',
            },
            files: [],
            scanInfo: { scannedAt: 0, skipped: 0, count: 0 },
            selectedFileId: settings.selectedFileId || '',
            selectedSegmentKey: settings.selectedSegmentKey || 'log',
            selectedPlayerGuid: settings.selectedPlayerGuid || '',
            // The fight used to be a tab before it moved into the scope
            // dropdown, so a stored 'encounters' would select nothing.
            activeTab: TABS.includes(settings.activeTab) ? settings.activeTab : 'overview',
            sortKey: settings.sortKey || 'lastModified',
            sortDirection: settings.sortDirection || 'desc',
            onlyWowLogs: settings.onlyWowLogs !== false,
            showNonGroupPlayers: settings.showNonGroupPlayers === true,
            monitorEnabled: settings.monitorEnabled !== false,
            autoAnalyzeNewest: settings.autoAnalyzeNewest !== false,
            deathWindowSeconds: settings.deathWindowSeconds || 10,
            linkedCharacterId: settings.linkedCharacterId || '',
            reportCacheMb: settings.reportCacheMb || 64,
            // Every run and encounter found in any analyzed log, so one run can
            // be put next to another without opening its file first.
            runCatalog: [],
            compareKey: '',
            compareReport: null,
            compareLoading: false,
            reportCache: { files: 0, reports: 0, bytes: 0, budgetBytes: 0 },
            summaries: {},
            reports: {},
            progress: {},
            processingFileId: '',
            lastUpdateAt: 0,
            storage: { usage: 0, quota: 0 },
            error: '',
            notice: '',
        }
    },

    getters: {
        isSupported(state) {
            return state.supported.directory
        },
        visibleFiles(state) {
            const files = state.onlyWowLogs ? state.files.filter((file) => file.isWowLog) : state.files
            const direction = state.sortDirection === 'asc' ? 1 : -1
            const key = state.sortKey
            return [...files].sort((left, right) => {
                if (key === 'name') return left.name.localeCompare(right.name) * direction
                if (key === 'size') return (left.size - right.size) * direction
                if (key === 'status') return String(left.status).localeCompare(String(right.status)) * direction
                return (left.lastModified - right.lastModified) * direction
            })
        },
        selectedFile(state) {
            return state.files.find((file) => file.id === state.selectedFileId) || null
        },
        selectedSummary(state) {
            return state.summaries[state.selectedFileId] || null
        },
        selectedReport(state) {
            return state.reports[`${state.selectedFileId}::${state.selectedSegmentKey}`] || null
        },
        selectedProgress(state) {
            return state.progress[state.selectedFileId] || null
        },
        isProcessing(state) {
            return Boolean(state.processingFileId)
        },
        monitorLabel(state) {
            if (!state.monitor.running) return 'Stopped'
            if (state.monitor.paused) return 'Paused'
            if (state.monitor.observerActive) return 'Live (file system observer)'
            return `Polling every ${Math.round((state.monitor.intervalMs || 0) / 1000)}s`
        },
    },

    mutations: {
        setInitialized(state, value) {
            state.initialized = Boolean(value)
        },
        setSupported(state, supported) {
            state.supported = { ...state.supported, ...supported }
        },
        setMode(state, mode) {
            state.mode = mode
        },
        setUsesWorker(state, value) {
            state.usesWorker = Boolean(value)
        },
        setDirectory(state, directory) {
            state.directory = { ...state.directory, ...directory }
        },
        setMonitorState(state, monitorState) {
            state.monitor = { ...state.monitor, ...monitorState }
        },
        setFiles(state, files) {
            state.files = files
        },
        setScanInfo(state, info) {
            state.scanInfo = { ...state.scanInfo, ...info }
        },
        setSelectedFile(state, fileId) {
            state.selectedFileId = fileId
        },
        setSelectedSegment(state, key) {
            state.selectedSegmentKey = key
        },
        setSelectedPlayer(state, guid) {
            state.selectedPlayerGuid = guid
        },
        setActiveTab(state, tab) {
            state.activeTab = tab
        },
        setSort(state, { key, direction }) {
            state.sortKey = key
            state.sortDirection = direction
        },
        setOnlyWowLogs(state, value) {
            state.onlyWowLogs = Boolean(value)
        },
        setShowNonGroupPlayers(state, value) {
            state.showNonGroupPlayers = Boolean(value)
        },
        setMonitorEnabled(state, value) {
            state.monitorEnabled = Boolean(value)
        },
        setAutoAnalyzeNewest(state, value) {
            state.autoAnalyzeNewest = Boolean(value)
        },
        setDeathWindow(state, seconds) {
            state.deathWindowSeconds = Number(seconds) || 10
        },
        setLinkedCharacter(state, id) {
            state.linkedCharacterId = id || ''
        },
        setReportCacheMb(state, value) {
            state.reportCacheMb = Number(value) || 64
        },
        setReportCache(state, stats) {
            state.reportCache = { ...state.reportCache, ...stats }
        },
        setRunCatalog(state, runs) {
            state.runCatalog = runs
        },
        setCompare(state, { key, report }) {
            state.compareKey = key || ''
            state.compareReport = report || null
        },
        setCompareLoading(state, value) {
            state.compareLoading = Boolean(value)
        },
        setSummary(state, { fileId, summary }) {
            state.summaries = { ...state.summaries, [fileId]: summary }
        },
        setReport(state, { fileId, segmentKey, report }) {
            state.reports = { ...state.reports, [`${fileId}::${segmentKey}`]: report }
        },
        dropFileData(state, fileId) {
            const summaries = { ...state.summaries }
            delete summaries[fileId]
            const reports = { ...state.reports }
            Object.keys(reports).forEach((key) => {
                if (key.startsWith(`${fileId}::`)) delete reports[key]
            })
            const progress = { ...state.progress }
            delete progress[fileId]
            state.summaries = summaries
            state.reports = reports
            state.progress = progress
        },
        dropReportsFor(state, fileId) {
            const reports = { ...state.reports }
            Object.keys(reports).forEach((key) => {
                if (key.startsWith(`${fileId}::`)) delete reports[key]
            })
            state.reports = reports
        },
        // Mirrors an eviction in the database so the view never shows a report
        // that is no longer stored anywhere.
        dropReportsExcept(state, keepFileId) {
            const reports = {}
            Object.keys(state.reports).forEach((key) => {
                if (key.startsWith(`${keepFileId}::`)) reports[key] = state.reports[key]
            })
            state.reports = reports
        },
        setProgress(state, { fileId, value }) {
            if (!value) {
                const next = { ...state.progress }
                delete next[fileId]
                state.progress = next
                return
            }
            state.progress = { ...state.progress, [fileId]: value }
        },
        setProcessingFile(state, fileId) {
            state.processingFileId = fileId || ''
        },
        setLastUpdateAt(state, value) {
            state.lastUpdateAt = value
        },
        setStorage(state, storage) {
            state.storage = storage
        },
        setError(state, message) {
            state.error = message || ''
        },
        setNotice(state, message) {
            state.notice = message || ''
        },
    },

    actions: {
        persistSettings({ state }) {
            writeSettings({
                directoryName: state.directory.name,
                monitorEnabled: state.monitorEnabled,
                selectedFileId: state.selectedFileId,
                selectedSegmentKey: state.selectedSegmentKey,
                selectedPlayerGuid: state.selectedPlayerGuid,
                activeTab: state.activeTab,
                sortKey: state.sortKey,
                sortDirection: state.sortDirection,
                autoAnalyzeNewest: state.autoAnalyzeNewest,
                showNonGroupPlayers: state.showNonGroupPlayers,
                onlyWowLogs: state.onlyWowLogs,
                deathWindowSeconds: state.deathWindowSeconds,
                linkedCharacterId: state.linkedCharacterId,
                reportCacheMb: state.reportCacheMb,
            })
        },

        async initialize({ state, commit, dispatch }) {
            if (state.initialized) {
                // A revisit of the view still refreshes, so a change made while
                // the user was on another page shows up immediately.
                if (state.mode === 'directory' && directoryHandle) await dispatch('refresh')
                return
            }

            commit('setSupported', {
                directory: supportsDirectoryAccess(),
                observer: supportsFileSystemObserver(),
                persistence: supportsHandlePersistence(),
            })
            commit('setInitialized', true)

            dispatch('attachClientListeners')
            dispatch('attachBroadcast')

            if (!supportsDirectoryAccess()) {
                commit('setMode', 'manual')
                await dispatch('loadStoredFiles', MANUAL_DIRECTORY_ID)
                await dispatch('openStoredSelection')
                return
            }

            commit('setMode', 'directory')

            try {
                const stored = await loadStoredDirectory()
                if (!stored) {
                    await dispatch('loadStoredFiles', MANUAL_DIRECTORY_ID)
                    await dispatch('openStoredSelection')
                    return
                }

                directoryHandle = stored.handle
                const permission = await queryAccess(stored.handle)
                commit('setDirectory', {
                    id: stored.meta.directoryId,
                    name: stored.meta.name || stored.handle.name,
                    connected: true,
                    permission,
                    connectedAt: stored.meta.connectedAt || 0,
                })

                await dispatch('loadStoredFiles', stored.meta.directoryId)

                if (permission === 'granted') {
                    await dispatch('startMonitoring')
                }

                await dispatch('openStoredSelection')
            } catch (error) {
                commit('setError', error.message || 'Unable to restore the saved folder')
            }
        },

        // The log that was open last time is opened again, so a visit starts on
        // its analysis rather than on an empty card: the summary is restored,
        // the last fight is picked and, if nothing can be shown from what is
        // stored, the log is read again without being asked for.
        async openStoredSelection({ state, dispatch }) {
            const fileId = state.selectedFileId
            if (!fileId) return
            if (!state.files.some((file) => file.id === fileId)) return
            await dispatch('selectFile', fileId)
        },

        attachClientListeners({ commit, state, dispatch }) {
            const analyzerClient = getClient()
            unsubscribe.forEach((off) => off())
            unsubscribe = [
                analyzerClient.on('progress', (message) => {
                    commit('setProgress', {
                        fileId: message.fileId,
                        value: {
                            offset: message.offset,
                            size: message.size,
                            events: message.events,
                            elapsedMs: message.elapsedMs,
                        },
                    })
                }),
                analyzerClient.on('status', (message) => {
                    if (message.status === 'processing') commit('setProcessingFile', message.fileId)
                }),
                analyzerClient.on('warning', (message) => {
                    commit('setNotice', message.message || '')
                }),
                analyzerClient.on('error', (message) => {
                    commit('setError', message.message || 'Log analysis failed')
                }),
                analyzerClient.on('fallback', () => {
                    commit('setUsesWorker', false)
                }),
                analyzerClient.on('cacheEvicted', (message) => {
                    // The log files are untouched; only their cached reports
                    // went, so say what actually happened.
                    const evicted = message.fileIds || []
                    evicted.forEach((fileId) => commit('dropReportsFor', fileId))
                    commit('setNotice', `Report cache full: the stored analysis of ${evicted.length} older ${evicted.length === 1 ? 'log' : 'logs'} was dropped. They are re-read when opened.`)
                    dispatch('refreshReportCache')
                }),
            ]
            commit('setUsesWorker', analyzerClient.usesWorker === true)
            analyzerClient.configure({
                reportBudgetBytes: state.reportCacheMb * 1024 * 1024,
                keepFileId: state.selectedFileId,
            })
            if (state.mode === 'directory' && !directoryHandle) dispatch('persistSettings')
        },

        // Other tabs of the same app analyze the same folder. A short message is
        // enough: whoever receives it reloads the stored records instead of
        // re-reading the file.
        attachBroadcast({ state, commit, dispatch }) {
            if (typeof BroadcastChannel === 'undefined' || broadcast) return
            broadcast = new BroadcastChannel('rio-combat-logs')
            broadcast.onmessage = async (event) => {
                const message = event.data
                if (!message || message.type !== 'analysis-updated') return
                if (message.fileId === state.selectedFileId) {
                    await dispatch('loadStoredAnalysis', message.fileId)
                }
                commit('setLastUpdateAt', Date.now())
                await dispatch('loadStoredFiles', state.directory.id || MANUAL_DIRECTORY_ID)
            }
        },

        async loadStoredFiles({ state, commit }, directoryId) {
            const id = directoryId || state.directory.id || MANUAL_DIRECTORY_ID
            try {
                const records = await listFileRecords(id)
                const scannedByName = new Map(state.files.map((file) => [file.name, file]))
                const files = records.map((record) => {
                    const known = scannedByName.get(record.name)
                    return toFileEntry(id, known && known.available ? {
                        name: known.name,
                        size: known.size,
                        lastModified: known.lastModified,
                        isWowLog: known.isWowLog,
                    } : null, record)
                })

                // Keep files that exist on disk but have no record yet.
                state.files.forEach((file) => {
                    if (!files.some((entry) => entry.name === file.name)) files.push(file)
                })

                commit('setFiles', files)
            } catch (error) {
                commit('setError', error.message || 'Unable to read stored analyses')
            }
        },

        async connectDirectory({ commit, dispatch }) {
            try {
                commit('setError', '')
                const { handle, meta } = await pickDirectory()
                directoryHandle = handle
                fileHandles.clear()
                commit('setDirectory', {
                    id: meta.directoryId,
                    name: meta.name,
                    connected: true,
                    permission: 'granted',
                    connectedAt: meta.connectedAt,
                })
                commit('setMode', 'directory')
                await dispatch('loadStoredFiles', meta.directoryId)
                await dispatch('startMonitoring')
                dispatch('persistSettings')
            } catch (error) {
                if (error && error.name === 'AbortError') return
                commit('setError', error.message || 'Folder selection failed')
            }
        },

        async requestDirectoryAccess({ state, commit, dispatch }) {
            if (!directoryHandle) return
            const permission = await requestAccess(directoryHandle)
            commit('setDirectory', { permission })
            if (permission === 'granted') {
                await dispatch('startMonitoring')
            } else {
                commit('setError', 'The browser did not grant read access to the folder')
            }
        },

        async disconnectDirectory({ commit, dispatch }) {
            await dispatch('stopMonitoring')
            await forgetDirectory()
            directoryHandle = null
            fileHandles.clear()
            commit('setDirectory', { id: '', name: '', connected: false, permission: 'prompt', connectedAt: 0 })
            commit('setFiles', [])
            commit('setSelectedFile', '')
            dispatch('persistSettings')
        },

        async startMonitoring({ state, commit, dispatch }) {
            if (!directoryHandle) return
            if (!monitor) {
                monitor = createMonitor({
                    onScan: (result) => dispatch('applyScan', result),
                    onState: (monitorState) => commit('setMonitorState', monitorState),
                    onError: (error) => commit('setError', error.message || 'Folder scan failed'),
                })
            } else {
                monitor.stop()
            }

            await monitor.start(directoryHandle)
            if (!state.monitorEnabled) monitor.pause()
        },

        async stopMonitoring({ commit }) {
            if (monitor) {
                monitor.stop()
                monitor = null
            }
            commit('setMonitorState', { mode: 'idle', running: false, paused: false })
        },

        async pauseMonitoring({ commit, dispatch }) {
            if (monitor) monitor.pause()
            commit('setMonitorEnabled', false)
            dispatch('persistSettings')
        },

        async resumeMonitoring({ commit, dispatch }) {
            commit('setMonitorEnabled', true)
            dispatch('persistSettings')
            if (monitor) await monitor.resume()
            else await dispatch('startMonitoring')
        },

        async refresh({ dispatch }) {
            if (monitor) {
                await monitor.scanNow('manual')
                return
            }
            if (!directoryHandle) return
            const result = await scanDirectory(directoryHandle)
            await dispatch('applyScan', { ...result, reason: 'manual' })
        },

        // Reconciles a directory listing with the stored analyses and decides
        // what needs (re)processing.
        async applyScan({ state, commit, dispatch }, result) {
            const directoryId = state.directory.id
            if (!directoryId) return

            const records = await listFileRecords(directoryId)
            const recordByName = new Map(records.map((record) => [record.name, record]))
            const files = []

            result.files.forEach((scanned) => {
                fileHandles.set(makeFileId(directoryId, scanned.name), scanned.handle)
                files.push(toFileEntry(directoryId, scanned, recordByName.get(scanned.name) || null))
            })

            records.forEach((record) => {
                if (result.files.some((scanned) => scanned.name === record.name)) return
                // Analyzed earlier but no longer in the folder: keep the results,
                // mark it as missing so the list can say so.
                files.push({ ...toFileEntry(directoryId, null, record), available: false })
            })

            commit('setFiles', files)
            commit('setScanInfo', { scannedAt: result.scannedAt, skipped: result.skipped, count: result.files.length })
            commit('setLastUpdateAt', Date.now())

            const newest = [...files]
                .filter((file) => file.available && file.isWowLog)
                .sort((left, right) => right.lastModified - left.lastModified)[0]

            // Opening the view lands on the newest log in the folder, the one
            // that was just played, rather than on whatever was open last time.
            const openNewest = newest && state.autoAnalyzeNewest
                && (!state.selectedFileId || (!openedNewestLog && newest.id !== state.selectedFileId))
            if (openNewest) {
                openedNewestLog = true
                await dispatch('selectFile', newest.id)
                return
            }
            if (newest) openedNewestLog = true

            // The log on screen is never left waiting behind a button: one that
            // grew is brought up to date from its new bytes alone, and one that
            // has never been read is read now. The newest log is only picked up
            // on its own while that setting is on, which is what it is for.
            const pending = files.filter((file) => {
                if (!file.available || (file.status !== 'updated' && file.status !== 'new')) return false
                if (file.id === state.selectedFileId) return true
                return state.autoAnalyzeNewest && newest && file.id === newest.id
            })
            for (const file of pending) {
                await dispatch('analyzeFile', { fileId: file.id })
            }
        },

        async selectFile({ state, commit, dispatch }, fileId) {
            if (!fileId) return
            commit('setSelectedFile', fileId)
            commit('setSelectedSegment', 'log')
            commit('setSelectedPlayer', '')
            focusLatest.add(fileId)
            dispatch('persistSettings')

            const file = state.files.find((entry) => entry.id === fileId)
            if (!file) return

            await dispatch('loadStoredAnalysis', fileId)
            await dispatch('focusLatestSegment', fileId)

            if (!file.available) return

            if (file.status === 'new' || file.status === 'updated' || file.status === 'error') {
                await dispatch('analyzeFile', { fileId })
                return
            }

            // A log that was analyzed before but has nothing left to show from
            // it, because its cached report was evicted and its snapshot could
            // not be rebuilt, is read again here rather than put behind a
            // button: the view is never a dead end.
            if (!state.reports[`${fileId}::${state.selectedSegmentKey}`]) {
                await dispatch('analyzeFile', { fileId })
            }
        },

        async loadStoredAnalysis({ commit }, fileId) {
            const analyzerClient = getClient()
            const stored = await analyzerClient.storedSummary(fileId)
            if (stored && stored.summary) {
                commit('setSummary', { fileId, summary: stored.summary })
                const report = await getReport(fileId, 'log')
                if (report) commit('setReport', { fileId, segmentKey: 'log', report })
            }
        },

        // Moves a freshly opened log to its last run. It is a no-op once the
        // user has a fight of their own on screen, and once there is nothing
        // but the whole log to show.
        async focusLatestSegment({ state, commit, dispatch }, fileId) {
            if (!focusLatest.has(fileId)) return
            const summary = state.summaries[fileId]
            if (!summary) return

            const segmentKey = latestSegmentKey(summary)
            focusLatest.delete(fileId)
            if (segmentKey === state.selectedSegmentKey) return

            commit('setSelectedSegment', segmentKey)
            dispatch('persistSettings')
            await dispatch('loadSegment', { fileId, segmentKey })
        },

        // Whatever path led here, a selected log is never left on an empty card
        // with a button on it: the chosen fight is fetched, a log that can still
        // be read is read, and the whole log stands in for a fight whose report
        // cannot be rebuilt at all.
        async ensureReport({ state, commit, dispatch }) {
            const fileId = state.selectedFileId
            if (!fileId || state.processingFileId === fileId) return

            const hasReport = () => Boolean(state.reports[`${fileId}::${state.selectedSegmentKey}`])
            if (hasReport()) return

            await dispatch('loadSegment', { fileId, segmentKey: state.selectedSegmentKey })
            if (hasReport()) return

            const file = state.files.find((entry) => entry.id === fileId)
            if (file && file.available) {
                await dispatch('analyzeFile', { fileId })
                if (hasReport()) return
            }

            if (state.selectedSegmentKey !== 'log') {
                commit('setSelectedSegment', 'log')
                await dispatch('loadSegment', { fileId, segmentKey: 'log' })
            }
        },

        async selectSegment({ state, commit, dispatch }, segmentKey) {
            commit('setSelectedSegment', segmentKey)
            dispatch('persistSettings')
            await dispatch('loadSegment', { fileId: state.selectedFileId, segmentKey })
        },

        async loadSegment({ state, commit }, { fileId, segmentKey }) {
            if (!fileId || !segmentKey) return
            const key = `${fileId}::${segmentKey}`
            if (state.reports[key]) return
            const analyzerClient = getClient()
            const report = await analyzerClient.segment(fileId, segmentKey)
            if (report) commit('setReport', { fileId, segmentKey, report })
        },

        async analyzeFile({ state, commit, dispatch }, { fileId, force = false }) {
            const entry = state.files.find((file) => file.id === fileId)
            if (!entry) return

            let file = null
            if (entry.directoryId === MANUAL_DIRECTORY_ID) {
                file = manualFiles.get(fileId) || null
            } else {
                const handle = fileHandles.get(fileId)
                file = await getFileFromHandle(handle)
            }

            if (!file) {
                commit('setError', `${entry.name} is not readable any more. Refresh the folder or re-grant access.`)
                return
            }

            commit('setProcessingFile', fileId)
            commit('setError', '')

            try {
                // A Web Lock keeps two tabs from analyzing the same file at once.
                const run = async () => {
                    const analyzerClient = getClient()
                    return analyzerClient.process({
                        fileId,
                        fileName: entry.name,
                        directoryId: entry.directoryId,
                        file,
                        force,
                    })
                }

                const summary = typeof navigator !== 'undefined' && navigator.locks
                    ? await navigator.locks.request(`rio-combat-log:${fileId}`, run)
                    : await run()

                if (summary) {
                    commit('setSummary', { fileId, summary })
                    commit('setLastUpdateAt', Date.now())
                    if (broadcast) broadcast.postMessage({ type: 'analysis-updated', fileId })
                }

                await dispatch('loadStoredFiles', entry.directoryId)
                await dispatch('focusLatestSegment', fileId)

                if (fileId === state.selectedFileId) {
                    const segmentKey = state.selectedSegmentKey
                    const analyzerClient = getClient()
                    const report = await analyzerClient.segment(fileId, segmentKey)
                    if (report) commit('setReport', { fileId, segmentKey, report })
                    else if (segmentKey !== 'log') commit('setSelectedSegment', 'log')
                }
            } catch (error) {
                commit('setError', error.message || `Unable to analyze ${entry.name}`)
            } finally {
                commit('setProcessingFile', '')
                commit('setProgress', { fileId, value: null })
            }
        },

        async reanalyzeFile({ dispatch, commit }, fileId) {
            commit('dropFileData', fileId)
            await dispatch('analyzeFile', { fileId, force: true })
        },

        cancelAnalysis({ state }) {
            if (!state.processingFileId) return
            getClient().cancel(state.processingFileId)
        },

        // Manual mode: the same engine, the same reports, only the file source
        // differs. Used when the browser has no directory access, and as an
        // escape hatch for one-off files from anywhere.
        async addManualFiles({ state, commit, dispatch }, fileList) {
            const incoming = [...(fileList || [])].filter((file) => /\.txt$/i.test(file.name))
            if (!incoming.length) {
                commit('setError', 'Only .txt combat log files can be analyzed')
                return
            }

            const files = [...state.files]
            incoming.forEach((file) => {
                const id = makeFileId(MANUAL_DIRECTORY_ID, file.name)
                manualFiles.set(id, file)
                const existing = files.findIndex((entry) => entry.id === id)
                const entry = {
                    id,
                    name: file.name,
                    directoryId: MANUAL_DIRECTORY_ID,
                    size: file.size,
                    lastModified: file.lastModified,
                    available: true,
                    isWowLog: looksLikeWowLog(file.name),
                    status: 'new',
                    offset: 0,
                    analyzedAt: 0,
                    error: '',
                    summary: null,
                }
                if (existing >= 0) files.splice(existing, 1, entry)
                else files.push(entry)
            })

            commit('setFiles', files)
            commit('setMode', state.supported.directory && directoryHandle ? 'directory' : 'manual')

            const first = incoming[0]
            const firstId = makeFileId(MANUAL_DIRECTORY_ID, first.name)
            await dispatch('selectFile', firstId)

            for (const file of incoming.slice(1)) {
                await dispatch('analyzeFile', { fileId: makeFileId(MANUAL_DIRECTORY_ID, file.name) })
            }
        },

        async removeFileAnalysis({ state, commit, dispatch }, fileId) {
            await deleteFileRecord(fileId)
            getClient().drop(fileId)
            commit('dropFileData', fileId)
            if (state.selectedFileId === fileId) commit('setSelectedFile', '')
            await dispatch('loadStoredFiles', state.directory.id || MANUAL_DIRECTORY_ID)
        },

        async clearStoredAnalyses({ state, commit, dispatch }) {
            await clearAll()
            directoryHandle = null
            fileHandles.clear()
            manualFiles.clear()
            await dispatch('stopMonitoring')
            commit('setFiles', [])
            commit('setSelectedFile', '')
            commit('setDirectory', { id: '', name: '', connected: false, permission: 'prompt' })
            Object.keys(state.summaries).forEach((fileId) => commit('dropFileData', fileId))
            await dispatch('refreshStorageUsage')
        },

        async refreshStorageUsage({ commit }) {
            commit('setStorage', await estimateUsage())
        },

        // The catalog is read from the stored summaries, so a run recorded in a
        // log that is not open can still be compared without re-reading it.
        async loadRunCatalog({ state, commit }) {
            try {
                const records = await listFileRecords(state.directory.id || MANUAL_DIRECTORY_ID)
                const runs = []
                records.forEach((record) => {
                    const summary = record.summary
                    if (!summary || !summary.segments) return
                    summary.segments.forEach((segment) => {
                        if (segment.type === 'log') return
                        runs.push({
                            key: `${record.id}::${segment.key}`,
                            fileId: record.id,
                            fileName: summary.fileName || record.name,
                            segmentKey: segment.key,
                            type: segment.type,
                            name: segment.name,
                            meta: segment.meta || {},
                            startTs: segment.startTs,
                            durationMs: segment.durationMs,
                            fightMs: segment.fightMs,
                            deathCount: segment.deathCount,
                            playerCount: segment.playerCount,
                            totals: segment.totals,
                        })
                    })
                })
                runs.sort((left, right) => right.startTs - left.startTs)
                commit('setRunCatalog', runs)
            } catch (error) {
                commit('setError', error.message || 'Unable to read the stored runs')
            }
        },

        async selectCompareRun({ commit }, key) {
            if (!key) {
                commit('setCompare', { key: '', report: null })
                return
            }

            const separator = key.lastIndexOf('::')
            const fileId = key.slice(0, separator)
            const segmentKey = key.slice(separator + 2)

            commit('setCompareLoading', true)
            try {
                const report = await getClient().segment(fileId, segmentKey)
                commit('setCompare', { key: report ? key : '', report })
                if (!report) {
                    commit('setNotice', 'That run is no longer in the report cache. Open its log once and it comes back.')
                }
            } catch (error) {
                commit('setError', error.message || 'Unable to load the run to compare')
            } finally {
                commit('setCompareLoading', false)
            }
        },

        async refreshReportCache({ commit }) {
            try {
                commit('setReportCache', await getClient().cacheStats())
            } catch (error) {
                // The figure is informational; a failure to read it must not
                // interrupt anything the user is doing.
            }
        },

        setReportCacheMb({ state, commit, dispatch }, value) {
            commit('setReportCacheMb', value)
            dispatch('persistSettings')
            getClient().configure({
                reportBudgetBytes: state.reportCacheMb * 1024 * 1024,
                keepFileId: state.selectedFileId,
            })
            return dispatch('refreshReportCache')
        },

        async clearReportCache({ state, commit, dispatch }) {
            const result = await getClient().clearReportCache(state.selectedFileId)
            commit('dropReportsExcept', state.selectedFileId)
            commit('setNotice', `Freed ${Math.round((result.freedBytes || 0) / 1048576)} MB of cached reports. The logs themselves are untouched.`)
            commit('setReportCache', result.stats || {})
            await dispatch('refreshStorageUsage')
        },

        setSort({ commit, dispatch }, payload) {
            commit('setSort', payload)
            dispatch('persistSettings')
        },
        setActiveTab({ commit, dispatch }, tab) {
            commit('setActiveTab', tab)
            dispatch('persistSettings')
        },
        setSelectedPlayer({ commit, dispatch }, guid) {
            commit('setSelectedPlayer', guid)
            dispatch('persistSettings')
        },
        setOnlyWowLogs({ commit, dispatch }, value) {
            commit('setOnlyWowLogs', value)
            dispatch('persistSettings')
        },
        setShowNonGroupPlayers({ commit, dispatch }, value) {
            commit('setShowNonGroupPlayers', value)
            dispatch('persistSettings')
        },
        setAutoAnalyzeNewest({ commit, dispatch }, value) {
            commit('setAutoAnalyzeNewest', value)
            dispatch('persistSettings')
        },
        setDeathWindow({ commit, dispatch }, seconds) {
            commit('setDeathWindow', seconds)
            dispatch('persistSettings')
        },
        setLinkedCharacter({ commit, dispatch }, id) {
            commit('setLinkedCharacter', id)
            dispatch('persistSettings')
        },
        dismissNotice({ commit }) {
            commit('setNotice', '')
        },
        dismissError({ commit }) {
            commit('setError', '')
        },

        teardown({ dispatch }) {
            unsubscribe.forEach((off) => off())
            unsubscribe = []
            return dispatch('stopMonitoring')
        },
    },
}
